import React from "react";
import {
  CheckCircleOutlined,
  TrophyOutlined,
  FileTextOutlined,
  UserOutlined,
  SafetyOutlined,
  BulbOutlined,
  RiseOutlined,
  FallOutlined,
  FileSearchOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { JobApplication, RejectionReason } from "../Entities/job-application.entity";
import { REJECTION_REASON_CONFIG, ANALYTICS_CONSTANTS } from "./analytics-constants";

// Analytics data interfaces
export interface FunnelStage {
  name: string;
  count: number;
  percentage: number;
  icon: React.ReactNode;
  colorClass: string;
}

export interface ProfileInsights {
  averageScore: number;
  qualificationRate: number;
  scoreTrend: "improving" | "declining" | "stable" | "insufficient";
  recentScores: number[];
}

export interface RejectionAnalysis {
  reason: RejectionReason | "unknown";
  count: number;
  label: string;
  tip: string;
  icon: React.ReactNode;
}

export interface SmartRecommendation {
  priority: "high" | "medium" | "low";
  title: string;
  description: string;
  icon: React.ReactNode;
}

export interface DropOff {
  from: string;
  to: string;
  dropRate: number;
}

/**
 * Calculates funnel stages from job applications
 */
export const calculateFunnelStages = (applications: JobApplication[]): FunnelStage[] => {
  if (applications.length === 0) return [];

  const total = applications.length;
  const profileMatched = applications.filter(
    (app) => app.profileMatchStatus?.status === "qualified"
  ).length;
  const assessmentPassed = applications.filter(
    (app) => app.questionnaireStatus === "qualified" || app.questionnaireStatus === "partially_qualified"
  ).length;
  const interviewed = applications.filter(
    (app) => app.status === "interview" || app.status === "shortlisted" || app.status === "hired"
  ).length;
  const hired = applications.filter((app) => app.status === "hired").length;

  return [
    { name: "Applied", count: total, percentage: 100, icon: <FileTextOutlined />, colorClass: "applied" },
    {
      name: "Profile Matched",
      count: profileMatched,
      percentage: total > 0 ? Math.round((profileMatched / total) * 100) : 0,
      icon: <UserOutlined />,
      colorClass: "profile",
    },
    {
      name: "Assessment Passed",
      count: assessmentPassed,
      percentage: profileMatched > 0 ? Math.round((assessmentPassed / profileMatched) * 100) : 0,
      icon: <CheckCircleOutlined />,
      colorClass: "assessment",
    },
    {
      name: "Interviewed",
      count: interviewed,
      percentage: assessmentPassed > 0 ? Math.round((interviewed / assessmentPassed) * 100) : 0,
      icon: <SafetyOutlined />,
      colorClass: "interview",
    },
    {
      name: "Hired",
      count: hired,
      percentage: interviewed > 0 ? Math.round((hired / interviewed) * 100) : 0,
      icon: <TrophyOutlined />,
      colorClass: "hired",
    },
  ];
};

/**
 * Finds the biggest drop-off point in the funnel
 */
export const findBiggestDropOff = (funnelData: FunnelStage[]): DropOff | null => {
  if (funnelData.length < 2) return null;
  let maxDrop = 0;
  let dropIndex = -1;

  for (let i = 1; i < funnelData.length; i++) {
    const prevCount = funnelData[i - 1].count;
    const currCount = funnelData[i].count;
    if (prevCount > 0) {
      const dropRate = ((prevCount - currCount) / prevCount) * 100;
      if (dropRate > maxDrop && prevCount > currCount) {
        maxDrop = dropRate;
        dropIndex = i;
      }
    }
  }

  if (dropIndex === -1 || maxDrop === 0) return null;
  return {
    from: funnelData[dropIndex - 1].name,
    to: funnelData[dropIndex].name,
    dropRate: Math.round(maxDrop),
  };
};

/**
 * Calculates profile insights from applications
 */
export const calculateProfileInsights = (applications: JobApplication[]): ProfileInsights => {
  const appsWithScore = applications.filter(
    (app) => app.profileMatchStatus?.score !== undefined && app.profileMatchStatus.score > 0
  );

  if (appsWithScore.length === 0) {
    return { averageScore: 0, qualificationRate: 0, scoreTrend: "insufficient", recentScores: [] };
  }

  const scores = appsWithScore.map((app) => app.profileMatchStatus!.score);
  const averageScore = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  const qualified = appsWithScore.filter((app) => app.profileMatchStatus?.status === "qualified").length;
  const qualificationRate = Math.round((qualified / appsWithScore.length) * 100);

  const sortedApps = [...appsWithScore].sort(
    (a, b) => new Date(b.applied_date || b.createdAt).getTime() - new Date(a.applied_date || a.createdAt).getTime()
  );
  const recentScores = sortedApps.slice(0, ANALYTICS_CONSTANTS.RECENT_SCORES_LIMIT).map((app) => app.profileMatchStatus!.score);

  let scoreTrend: "improving" | "declining" | "stable" | "insufficient" = "insufficient";
  if (recentScores.length >= ANALYTICS_CONSTANTS.MIN_SCORES_FOR_TREND) {
    const recentAvg = recentScores.slice(0, 2).reduce((a, b) => a + b, 0) / 2;
    const olderAvg = recentScores.slice(-2).reduce((a, b) => a + b, 0) / 2;
    const diff = recentAvg - olderAvg;
    if (diff > ANALYTICS_CONSTANTS.SCORE_TREND_IMPROVING_THRESHOLD) scoreTrend = "improving";
    else if (diff < ANALYTICS_CONSTANTS.SCORE_TREND_DECLINING_THRESHOLD) scoreTrend = "declining";
    else scoreTrend = "stable";
  }

  return { averageScore, qualificationRate, scoreTrend, recentScores };
};

/**
 * Analyzes rejection reasons from applications
 */
export const analyzeRejections = (applications: JobApplication[]): RejectionAnalysis[] => {
  const rejected = applications.filter((app) => app.status === "rejected");
  if (rejected.length === 0) return [];

  const reasonCounts: Record<string, number> = {};
  rejected.forEach((app) => {
    const reason = app.rejectionReason || "unknown";
    reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
  });

  return Object.entries(reasonCounts)
    .map(([reason, count]) => ({
      reason: reason as RejectionReason | "unknown",
      count,
      ...(REJECTION_REASON_CONFIG[reason as RejectionReason | "unknown"] || REJECTION_REASON_CONFIG.unknown),
    }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Generates smart recommendations based on application data
 */
export const generateRecommendations = (
  applications: JobApplication[],
  funnelData: FunnelStage[],
  profileInsights: ProfileInsights,
  rejectionAnalysis: RejectionAnalysis[]
): SmartRecommendation[] => {
  const recs: SmartRecommendation[] = [];

  if (applications.length === 0) {
    return [
      {
        priority: "high",
        title: "Start Applying",
        description: "Begin your job search by applying to positions that match your skills.",
        icon: <FileTextOutlined />,
      },
    ];
  }

  const profileMatchRate = funnelData[1]?.percentage || 0;
  if (profileMatchRate < ANALYTICS_CONSTANTS.PROFILE_MATCH_RATE_THRESHOLD && applications.length >= ANALYTICS_CONSTANTS.MIN_APPLICATIONS_FOR_RECOMMENDATIONS) {
    recs.push({
      priority: "high",
      title: "Improve Your Profile Match",
      description: `Only ${profileMatchRate}% of your applications pass profile screening. Update your resume to highlight relevant skills.`,
      icon: <UserOutlined />,
    });
  }

  const profileMatched = applications.filter((app) => app.profileMatchStatus?.status === "qualified").length;
  const assessmentPassed = applications.filter(
    (app) => app.questionnaireStatus === "qualified" || app.questionnaireStatus === "partially_qualified"
  ).length;
  if (profileMatched > 0) {
    const assessmentRate = Math.round((assessmentPassed / profileMatched) * 100);
    if (assessmentRate < ANALYTICS_CONSTANTS.ASSESSMENT_RATE_THRESHOLD && profileMatched >= ANALYTICS_CONSTANTS.MIN_APPLICATIONS_FOR_RECOMMENDATIONS) {
      recs.push({
        priority: "high",
        title: "Focus on Assessment Prep",
        description: `${100 - assessmentRate}% of your assessments need improvement. Practice before taking assessments.`,
        icon: <FileSearchOutlined />,
      });
    }
  }

  const violations = rejectionAnalysis.find((r) => r.reason === "ai_assessment_violation");
  if (violations && violations.count > 0) {
    recs.push({
      priority: "high",
      title: "Avoid Assessment Violations",
      description: "Ensure quiet environment, stable internet, and don't switch tabs.",
      icon: <ExclamationCircleOutlined />,
    });
  }

  if (profileInsights.scoreTrend === "declining") {
    recs.push({
      priority: "medium",
      title: "Profile Scores Declining",
      description: "Review job requirements more carefully before applying.",
      icon: <FallOutlined />,
    });
  } else if (profileInsights.scoreTrend === "improving") {
    recs.push({
      priority: "low",
      title: "Great Progress!",
      description: "Your profile match scores are improving. Keep it up!",
      icon: <RiseOutlined />,
    });
  }

  const hiredCount = applications.filter((app) => app.status === "hired").length;
  if (hiredCount > 0) {
    recs.push({
      priority: "low",
      title: "Congratulations!",
      description: `You've been hired ${hiredCount} time${hiredCount > 1 ? "s" : ""}!`,
      icon: <TrophyOutlined />,
    });
  }

  return recs.length > 0
    ? recs
    : [
        {
          priority: "low",
          title: "Keep Going!",
          description: "Continue applying to positions that match your skills.",
          icon: <BulbOutlined />,
        },
      ];
};

/**
 * Gets recent applications sorted by date
 */
export const getRecentApplications = (applications: JobApplication[], limit: number = ANALYTICS_CONSTANTS.RECENT_APPLICATIONS_LIMIT): JobApplication[] => {
  return [...applications]
    .sort((a, b) => new Date(b.applied_date || b.createdAt).getTime() - new Date(a.applied_date || a.createdAt).getTime())
    .slice(0, limit);
};
